<template>
  <div class="w-full h-full flex-col min-h-[350px]">
    <div class="bg-gradient-to-b from-[#B0D0FF]/70 h-36 w-[100%] absolute rounded-t-lg left-0 top-0 pointer-events-none" />
    <div class="flex items-center w-full">
      <Tag class="w-12 h-12 -ml-2" color="#000" />
      <span class="mt-1 ml-2 text-3xl font-bold text-black">{{ $t("Tags") }}</span>
      <div
        class="w-8 h-8 ml-auto mr-3 bg-white border rounded-full cursor-pointer has-tooltip dark:border-dark-divider-border dark:bg-dark-screen-background"
        @click="$emit('cancel')">
        <X color="#FF0000" class="w-6 h-6 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration" /><span
          class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
          {{ $t("Close") }}</span>
      </div>
    </div>
    <div class="text-common-grey">
      {{ $t("Search and add tags to your records. Use them to filter, search and manage your portfolio.") }}
    </div>

    <div class="mt-12 w-[270px] max-w-[270px]">
      <SelectInput
        v-if="!isAllRecords && !record && !records.length"
        v-model="object_type"
        :items="object_types"
        :value="object_types.filter((o) => o.type == object_type)[0].type"
        name="tags_filter"
        label-attr="label"
        value-attr="type"
        :allow-null="false"
        :is-box-background="true"
        :inline-label="true"
        :show-label="false"
        :classes="`w-full mb-2`"
        :label="$t('Filter by')"
        :placeholder="$t('Select object to filter with')" />
    </div>

    <div class="flex flex-row items-center w-full gap-2 mt-2">
      <TextInput
        v-model="tag_name"
        :only-alpha-numeric="false"
        :value="tag_name"
        class="w-[270px] max-w-[270px]"
        :is-box-background="true"
        :show-label="false"
        :auto-focus="false"
        input-classes="appearance-none block px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light bg-white focus:border-blue-light sm:text-sm"
        name="name"
        type="text"
        :placeholder="`${$store.state.data.tags.length ? $t('Search or create tag') : $t('Name your tag')}`"
        label=""
        @input="tag_name = $event.target.value" />
      <div v-if="tag_name" class="h-10 w-10 !p-0 flex items-center rounded-md border-none">
        <TextInput
          :value="new_tag.color"
          label=""
          classes="h-10 w-10 !p-0 flex items-center rounded-md border-none"
          input-classes="h-10 w-10 overflow-hidden appearance-none rounded-md !p-0 border-none"
          name="color"
          type="color"
          :placeholder="$t('Tag Color')"
          @input="new_tag.color = $event.target.value" />
      </div>
      <button
        v-if="tag_name"
        :class="{ 'opacity-30 pointer-events-none': isAllRecords && !confirm_assign_all }"
        :disabled="is_name_duplicated"
        class="primary-btn w-fit"
        @click="tag_to_be_edited ? editTag() : fullRecords?.length ? createTag() : createAndAssignTag()">
        <span>{{ tag_to_be_edited ? $t("Edit tag") : fullRecords?.length ? $t("Create tag") : $t("Create and assign") }}</span>
        <Check class="w-6 h-6 ml-2" aria-hidden="true" />
      </button>
      <button
        v-if="tag_to_be_edited"
        type="button"
        class="w-fit cancel-btn py-2.5 -ml-1"
        @click="
          () => {
            tag_to_be_edited = null
            tag_name = ''
          }
        ">
        <span>{{ $t("Cancel") }}</span>
      </button>
    </div>

    <div v-if="isAllRecords" class="flex items-center space-x-2 text-status-error-red mb-4 mt-4">
      {{ $t("Confirm assigning to") }} {{ count - excludedRecords.length }} {{ $t("out of") }} {{ count }} {{ $t("records in table") }}

      <input
        v-model="confirm_assign_all"
        type="checkbox"
        name="truncate"
        class="ml-2 form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
    </div>

    <div v-if="$store.state.data.tags.length == 0" class="flex justify-center w-full my-2 mt-32 text-black">
      {{ $t("You currently have no tags created. Use the input box above to create your first tag.") }}
    </div>

    <div
      v-else-if="isAllRecords || (fullRecords?.length && fullRecords?.length > 1)"
      class="relative mt-4 max-w-full overflow-x-auto"
      :class="{ 'opacity-30 pointer-events-none': isAllRecords && !confirm_assign_all }">
      <table class="min-w-full table-auto">
        <tbody
          class="h-full transition ease-in-out divide-y rounded-lg bg-light-box-background divide-light-divider-border dark:divide-dark-divider-border dark:bg-dark-box-background duration-side-menu-transition-duration">
          <tr class="w-full text-md font-bold transition ease-in-out rounded-lg shadow-sm text-gray-400 dark:text-dark-body-text">
            <td class="pr-6 py-3 h-12 truncate">
              {{ $t("Name") }}
            </td>
            <td class="px-6 py-3 h-12 truncate">
              {{ $t("Currently") }}
            </td>
            <td class="px-6 py-3 h-12 truncate">
              {{ $t("Assign to selected") }}
            </td>
            <td class="px-6 py-3 h-12 truncate">
              {{ $t("Remove from selected") }}
            </td>
            <td class="px-6 py-3 h-12 truncate">
              {{ $t("Undo") }}
            </td>
            <td class="px-1 py-3 h-12 truncate" />
          </tr>
          <tr
            v-for="tag in object_type == 'all' ? all_tags : all_tags.filter((t) => t.object_type == object_type)"
            :key="tag?.id"
            class="w-full font-bold text-sm transition ease-in-out rounded-lg shadow-sm text-light-body-text dark:text-dark-body-text">
            <td class="pr-6 py-3 h-12 truncate">
              <div
                :style="{
                  backgroundColor: `${$store.state.data.tags.find((t) => t.id == tag.id)?.color}12`
                }"
                class="inline-block truncate px-4 py-2 rounded-md">
                <div
                  class="w-[10px] h-[10px] rounded-full inline-block mt-0 mr-2 text-transparent"
                  :style="{
                    backgroundColor: $store.state.data.tags.find((t) => t.id == tag.id)?.color
                  }" />
                <span
                  :style="{
                    color: $store.state.data.tags.find((t) => t.id == tag.id)?.color
                  }"
                  class="text-sm">{{ tag.name }}</span>
              </div>
            </td>
            <td class="px-6 py-3 h-12 truncate">
              {{ $t("Applied to") }} {{ tag.applied_to }} {{ $t("of") }} {{ number_of_selected_records }}
            </td>
            <td class="px-6 py-3 h-12 truncate">
              <button
                :style="{
                  backgroundColor: tag.selected ? $store.state.data.tags.find((t) => t.id == tag.id)?.color : '#F5F8FA'
                }"
                class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium dark:text-white rounded-md dark:bg-dark-screen-background hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light"
                @click="handleAssignTag(tag)">
                <TagPlusIcon
                  class="w-6 h-6 -ml-2 mr-2"
                  :color="tag.selected ? '#FFFFFF' : $store.state.data.tags.find((t) => t.id == tag.id)?.color" />
                +{{ number_of_selected_records - tag.applied_to }}
              </button>
            </td>
            <td class="px-6 py-3 h-12 truncate">
              <button
                :style="{
                  backgroundColor: tag.unselected ? $store.state.data.tags.find((t) => t.id == tag.id)?.color : '#F5F8FA'
                }"
                class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium dark:text-white rounded-md bg-[#F5F8FA] dark:bg-dark-screen-background hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light"
                @click="handleUnassignTag(tag)">
                <TagMinusIcon
                  class="w-6 h-6 -ml-2 mr-2"
                  :color="tag.unselected ? '#FFFFFF' : $store.state.data.tags.find((t) => t.id == tag.id)?.color" />
                -{{ tag.applied_to }}
              </button>
            </td>
            <td class="px-6 py-3 h-12 truncate">
              <button
                class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium dark:text-white rounded-md bg-[#F5F8FA] dark:bg-dark-screen-background hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light"
                @click="undo(tag)">
                <Undo2 class="w-6 h-6 -ml-2 mr-2" />
                {{ tag.undo }}
              </button>
            </td>
            <td class="px-1 py-3 h-12">
              <div class="relative flex items-center">
                <div @click.stop="">
                  <Menu as="div" class="flex-shrink-0 ml-auto">
                    <MenuButton
                      class="inline-flex items-center justify-center text-gray-400 transition ease-in-out rounded duration-side-menu-transition-duration"
                      @click.stop="">
                      <span class="sr-only">{{ $t("Open options") }}</span>
                      <EllipsisVertical :color="$store.state.ui.darkMode ? '#fff' : ''" class="w-5 h-5 border-none" />
                    </MenuButton>
                    <transition
                      enter-active-class="transition duration-100 ease-out"
                      enter-from-class="transform scale-95 opacity-0"
                      enter-to-class="transform scale-100 opacity-100"
                      leave-active-class="transition duration-75 ease-in"
                      leave-from-class="transform scale-100 opacity-100"
                      leave-to-class="transform scale-95 opacity-0">
                      <MenuItems
                        class="absolute right-0 z-30 flex flex-col items-start content-center justify-center p-2 mx-3 mt-1 origin-top-right bg-white rounded-md shadow-lg outline-none dark:bg-dark-screen-background ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <MenuItem
                          v-slot="{ active }"
                          @click.prevent="
                            () => {
                              tag_to_be_edited = tag
                              tag_name = tag.name
                              new_tag.color = tag.color
                            }
                          ">
                          <div
                            :class="[
                              active ? 'text-gray-900 dark:text-dark-body-text' : 'text-gray-600 dark:text-dark-body-text',
                              'group flex items-center px-3 py-2 text-sm cursor-pointer'
                            ]">
                            <Pencil class="w-5 h-5 mr-4" aria-hidden="true" />
                            <span class="whitespace-nowrap">{{ $t("Edit Tag") }}</span>
                          </div>
                        </MenuItem>
                        <MenuItem
                          v-slot="{ active }"
                          @click.prevent="
                            () => {
                              tag_to_be_deleted = tag
                              is_delete_tags_modal_open = true
                            }
                          ">
                          <div
                            :class="[
                              active ? 'text-gray-900 dark:text-dark-body-text' : 'text-gray-600 dark:text-dark-body-text',
                              'group flex items-center px-3 py-2 text-sm cursor-pointer'
                            ]">
                            <Trash2 class="w-5 h-5 mr-4 text-red-500" aria-hidden="true" />
                            <span class="whitespace-nowrap">{{ $t("Delete Tag") }}</span>
                          </div>
                        </MenuItem>
                      </MenuItems>
                    </transition>
                  </Menu>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-else
      class="flex flex-wrap gap-2 mt-4 max-h-[180px] overflow-y-auto"
      :class="{ 'opacity-30 pointer-events-none': isAllRecords && !confirm_assign_all }">
      <div
        v-for="tag in object_type == 'all' ? all_tags : all_tags.filter((t) => t.object_type == object_type)"
        :key="tag?.id"
        class="flex focus:outline-none"
        :style="{ background: $store.state.data.tags.find((t) => t.id == tag)?.color }">
        <div v-if="selected_tags.includes(tag.id)" class="relative flex items-center group">
          <span
            class="flex items-center rounded-md px-2 py-1.5 border border-gray-300"
            :style="{
              background: $store.state.data.tags.find((t) => t.id == tag.id)?.color
            }">
            <Check class="block w-4 h-4 mr-2 text-white cursor-pointer group-hover:hidden" aria-hidden="true" />
            <X
              class="hidden w-4 h-4 mr-2 text-white cursor-pointer group-hover:block"
              aria-hidden="true"
              @click="(e) => handleSelectTag(false, tag)" />
            <span class="block text-white truncate">{{ tag.name }}</span>
          </span>
          <div
            class="absolute right-2 items-center justify-center hidden rounded-full bg-white w-5 h-5 cursor-pointer group-hover:flex"
            @click="
              () => {
                tag_to_be_edited = tag
                tag_name = tag.name
                new_tag.color = tag.color
              }
            ">
            <Pencil color="#899399" class="w-4 h-4" aria-hidden="true" />
          </div>
          <div
            class="absolute right-8 items-center justify-center hidden rounded-full bg-white w-5 h-5 cursor-pointer group-hover:flex"
            @click="
              () => {
                tag_to_be_deleted = tag
                is_delete_tags_modal_open = true
              }
            ">
            <Trash2 class="w-4 h-4 text-red-500" aria-hidden="true" />
          </div>
        </div>
        <div v-else class="relative flex items-center group">
          <span class="flex items-center rounded-md px-2 py-1.5 border border-gray-300">
            <div class="mr-3 w-3.5 h-3.5 rounded-sm" :style="{ background: tag.color }">
              <Check
                class="w-3.5 h-3.5 text-white hidden group-hover:block cursor-pointer"
                aria-hidden="true"
                @click="(e) => handleSelectTag(true, tag)" />
            </div>
            <span class="block text-black truncate">{{ tag.name }}</span>
          </span>
          <div
            class="absolute right-2 items-center justify-center hidden bg-white rounded-full w-5 h-5 pl-1 cursor-pointer group-hover:flex"
            @click="
              () => {
                tag_to_be_edited = tag
                tag_name = tag.name
                new_tag.color = tag.color
              }
            ">
            <Pencil color="#899399" class="w-4 h-4" aria-hidden="true" />
          </div>
          <div
            class="absolute right-8 items-center justify-center hidden bg-white rounded-full w-5 h-5 cursor-pointer group-hover:flex"
            @click="
              () => {
                tag_to_be_deleted = tag
                is_delete_tags_modal_open = true
              }
            ">
            <Trash2 class="w-4 h-4 text-red-500" aria-hidden="true" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="isAllRecords || (fullRecords?.length && fullRecords?.length > 1)" class="flex flex-row justify-end w-full gap-2 mt-6">
      <button class="px-3 cancel-btn w-fit" @click="$emit('cancel')">{{ $t("Cancel") }}</button>
      <button class="w-fit primary-btn" @click="confirmAssignTags()">{{ $t("Apply") }}</button>
    </div>
    <div v-else class="flex justify-center flex-1 w-full mt-4">
      <button class="px-3 cancel-btn w-fit" :class="[$store.state.data.tags.length == 0 ? '' : 'mt-20']" @click="$emit('cancel')">
        {{ $t("Close") }}
      </button>
    </div>
  </div>
  <!-- Delete Tags -->
  <Modal
    v-model:is-open="is_delete_tags_modal_open"
    max-width="max-w-[600px] mx-5"
    :use-toast="true"
    :default-success-msg="$t('Tag deleted successfully')">
    <template #form="{ close: closeModal, done: showIsDone }">
      <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
        {{ $t("Delete Tag") }}
      </h3>
      <div class="max-w-xl mt-2 text-sm text-gray-500">
        <p>{{ $t("Once you delete tag, you can't restore it.") }}</p>
      </div>
      <div class="mt-5 text-center">
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
          @click="confirmDeleteTag(showIsDone)">
          {{ $t("Delete Tag") }}
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-dark-body-text focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
          @click="closeModal">
          {{ $t("Cancel") }}
        </button>
      </div>
    </template>
  </Modal>
</template>
<script>
import dataService from "@/services/data.service"
import TextInput from "@/components/shared/TextInput.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import Modal from "@/components/shared/Modal.vue"
import TagPlusIcon from "@/components/shared/icons/TagPlusIcon.vue"
import TagMinusIcon from "@/components/shared/icons/TagMinusIcon.vue"
import { X, Check, Trash2, EllipsisVertical, Pencil, Undo2, Tag } from "lucide-vue-next"
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/vue"

export default {
  components: {
    Menu,
    MenuButton,
    MenuItems,
    MenuItem,
    Tag,
    TagPlusIcon,
    TagMinusIcon,
    EllipsisVertical,
    Undo2,
    Check,
    X,
    Trash2,
    Pencil,
    TextInput,
    SelectInput,
    Modal
  },
  props: {
    count: null,
    fullRecords: null,
    records: null,
    record: null,
    objectType: null,
    isAllRecords: null,
    excludedRecords: null,
    filter: null
  },
  emits: ["done", "cancel", "error"],
  data() {
    return {
      tag_name: "",
      number_of_selected_records: 0,
      object_type: this.objectType,
      object_types: [{ id: "all", type: "all", label: this.$t("All") }].concat(this.$store.state.object_types),
      all_tags: this.$store.state.data.tags,
      tag_to_be_deleted: null,
      tag_to_be_edited: null,
      new_tag: {
        name: "",
        object_type: null,
        is_public: "t",
        description: "",
        color: "#FF4646"
      },
      selected_tags: [],
      deselected_tags: [],
      backup_record: null,
      is_name_duplicated: null,
      is_delete_tags_modal_open: false,
      confirm_assign_all: false
    }
  },
  watch: {
    tag_name() {
      this.is_name_duplicated = false
      if (!this.tag_to_be_edited) {
        let arr = []
        this.all_tags = this.$store.state.data.tags
        this.all_tags.map((t) => {
          if (t.name.toLowerCase() == this.tag_name.toLowerCase()) {
            this.is_name_duplicated = true
          }
          if (t.name.toLowerCase().includes(this.tag_name.toLowerCase())) {
            arr.push(t)
          }
        })
        this.all_tags = arr
      }
    }
  },
  created() {
    this.all_tags.map((tag) => {
      tag["applied_to"] = 0
      tag["backup_applied_to"] = 0
      tag["undo"] = 0
      tag["selected"] = null
      tag["unselected"] = null
    })
    this.all_tags = this.all_tags.filter((t) => t.object_type == this.object_type)
    if (this.isAllRecords) {
      this.selected_tags = []
      this.number_of_selected_records = this.count
    } else {
      if (this.fullRecords?.length) {
        this.number_of_selected_records = this.fullRecords.length
        let allselected = []
        this.fullRecords.forEach((r) => {
          r.tags.map((id) => {
            let index = this.all_tags.findIndex((t) => t.id == id)
            if (index != -1) {
              this.all_tags[index].applied_to = this.all_tags[index].applied_to + 1
              this.all_tags[index].backup_applied_to = this.all_tags[index].backup_applied_to + 1
            }
          })
          allselected.push(...r.tags)
        })
        this.selected_tags = Array.from(new Set(allselected))
      } else if (this.records?.length) {
        this.number_of_selected_records = this.records.length
        let allselected = []
        this.records.forEach((r) => allselected.push(r))
        this.selected_tags = Array.from(new Set(allselected))
      } else {
        this.backup_record = this.$utils.clone(this.record)
        this.selected_tags = this.record.tags
      }
    }
  },
  methods: {
    createAndAssignTag() {
      this.new_tag.name = this.tag_name
      this.new_tag.object_type = this.object_type == "all" ? null : this.object_type
      dataService.createTag(this.new_tag).then((result) => {
        let tags = [result.data].concat(this.$store.state.data.tags)
        this.$store.commit("data/setTags", tags)
        this.selected_tags.push(result.data.id)
        this.assignTags()
        this.all_tags = this.$store.state.data.tags
        this.tag_name = ""
      })
    },
    createTag() {
      this.new_tag.name = this.tag_name
      this.new_tag.object_type = this.object_type == "all" ? null : this.object_type
      dataService.createTag(this.new_tag).then((result) => {
        result.data["applied_to"] = 0
        result.data["backup_applied_to"] = 0
        result.data["selected"] = null
        result.data["unselected"] = null
        result.data["undo"] = 0
        let tags = [result.data].concat(this.$store.state.data.tags)
        this.$store.commit("data/setTags", tags)
        this.all_tags = this.$store.state.data.tags
        this.tag_name = ""
      })
    },
    editTag() {
      this.tag_to_be_edited.name = this.tag_name
      this.tag_to_be_edited.color = this.new_tag.color
      dataService.updateTag(this.tag_to_be_edited.id, this.tag_to_be_edited).then(() => {
        let tags = this.$store.state.data.tags
        let index = tags.findIndex((t) => t.id == this.tag_to_be_edited.id)
        tags[index].name = this.tag_to_be_edited.name
        tags[index].color = this.tag_to_be_edited.color
        this.$store.commit("data/setTags", tags)
        this.tag_to_be_edited = null
        this.tag_name = ""
      })
    },
    handleAssignTag(tag) {
      this.undo(tag)
      let index = this.all_tags.findIndex((t) => t.id === tag.id)
      if (index != -1) {
        this.all_tags[index].selected = true
        this.all_tags[index].applied_to = this.fullRecords?.length
        this.all_tags[index].undo = -(this.fullRecords?.length - this.all_tags[index].backup_applied_to)
      }
      //this.assignTags()
    },
    handleUnassignTag(tag) {
      this.undo(tag)
      let index = this.all_tags.findIndex((t) => t.id === tag.id)
      if (index != -1) {
        this.all_tags[index].unselected = true
        this.all_tags[index].applied_to = 0
        this.all_tags[index].undo = +this.all_tags[index].backup_applied_to
      }
      //this.assignTags()
    },
    undo(tag) {
      let index = this.all_tags.findIndex((t) => t.id == tag.id)
      if (index != -1) {
        this.all_tags[index]["undo"] = 0
        this.all_tags[index]["applied_to"] = this.$utils.clone(this.all_tags[index].backup_applied_to)
        this.all_tags[index]["selected"] = null
        this.all_tags[index]["unselected"] = null
      }
      //this.assignTags()
    },
    confirmAssignTags() {
      let selected_tags = []
      let unselected_tags = []
      this.all_tags.map((tag) => {
        if (tag.selected) {
          selected_tags.push(tag.id)
        } else if (tag.unselected) {
          unselected_tags.push(tag.id)
        }
      })

      if (this.isAllRecords) {
        let data = {
          object_type: this.object_type,
          assigned_tags: selected_tags,
          unassigned_tags: unselected_tags,
          records: [],
          excluded_records: this.excludedRecords?.length ? this.excludedRecords : [],
          is_all_records: this.isAllRecords,
          filter: this.filter
        }
        dataService.bulkTagsAssign(data).then(() => {
          this.$emit("done")
        })
      } else if (this.fullRecords?.length) {
        let data = {
          assigned_tags: selected_tags,
          unassigned_tags: unselected_tags,
          records: this.records
        }
        dataService.bulkTagsAssign(data).then(() => {
          this.$emit("done")
        })
        let new_records = this.fullRecords.map((r) => {
          let selectedTags = Array.from(new Set(r.tags.concat(selected_tags)))
          r.tags = selectedTags.filter((id) => !unselected_tags.includes(id))
          return r
        })
        new_records.forEach((r) => {
          this.$store.dispatch("data/emitRecordUpdatedEmitters", r)
        })
      } else if (this.records?.length) {
        let data = {
          assigned_tags: selected_tags,
          unassigned_tags: unselected_tags,
          records: this.records
        }
        dataService.bulkTagsAssign(data).then(() => {
          this.$emit("done")
        })
      } else {
        if (this.record) {
          let new_record = { ...this.record }

          new_record.tags = selected_tags
          this.$store.dispatch("ui/setCurrentCompany", new_record)

          let data = { tags: selected_tags }

          this.$store.dispatch("data/updateRecord", { record_id: this.record.id, data: data }).then((res) => {
            this.$emit("done")
            this.$store.dispatch("data/emitRecordUpdatedEmitters", res.data)
          })
        }
      }
    },
    handleSelectTag(checked, tag) {
      if (checked) {
        this.selected_tags.push(tag.id)
      } else {
        let index = this.selected_tags.findIndex((r) => r === tag.id)
        this.selected_tags.splice(index, 1)
        this.deselected_tags.push(tag.id)
      }
      this.assignTags()
    },
    confirmDeleteTag(showIsDone) {
      dataService.deleteTag(this.tag_to_be_deleted.id).then(() => {
        if (this.records?.length) {
          let new_records = this.fullRecords.map((r) => {
            r.tags = r.tags.filter((tag_id) => tag_id != this.tag_to_be_deleted.id)
            return r
          })
          new_records.forEach((r) => {
            this.$store.dispatch("data/emitRecordUpdatedEmitters", r)
          })
        } else {
          if (this.record) {
            let new_record = { ...this.backup_record }
            new_record.tags = new_record.tags.filter((tag_id) => tag_id != this.tag_to_be_deleted.id)
            this.$store.dispatch("ui/setCurrentCompany", new_record)
            this.$store
              .dispatch("data/updateRecord", {
                record_id: this.record.id,
                data: new_record
              })
              .then((res) => {
                this.$store.dispatch("data/emitRecordUpdatedEmitters", res.data)
              })
          }
        }
        let tags = this.$store.state.data.tags.filter((tag) => tag.id != this.tag_to_be_deleted.id)
        this.all_tags = tags
        this.$store.commit("data/setTags", tags)
        showIsDone()
      })
    },
    assignTags() {
      if (this.isAllRecords) {
        let data = {
          object_type: this.object_type,
          assigned_tags: this.selected_tags,
          unassigned_tags: this.deselected_tags,
          records: [],
          excluded_records: this.excludedRecords?.length ? this.excludedRecords : [],
          is_all_records: this.isAllRecords,
          filter: this.filter
        }
        dataService.bulkTagsAssign(data).then(() => {
          //this.$emit("done")
        })
      } else if (this.fullRecords?.length) {
        let data = {
          assigned_tags: this.selected_tags,
          unassigned_tags: this.deselected_tags,
          records: this.records
        }
        dataService.bulkTagsAssign(data).then(() => {
          //this.$emit("done")
        })
        let new_records = this.fullRecords.map((r) => {
          r.tags = this.selected_tags
          return r
        })
        new_records.forEach((r) => {
          this.$store.dispatch("data/emitRecordUpdatedEmitters", r)
        })
      } else if (this.records?.length) {
        let data = {
          assigned_tags: this.selected_tags,
          unassigned_tags: this.deselected_tags,
          records: this.records
        }
        dataService.bulkTagsAssign(data).then(() => {
          //this.$emit("done")
        })
      } else {
        if (this.record) {
          let new_record = { ...this.record }

          new_record.tags = this.selected_tags
          this.$store.dispatch("ui/setCurrentCompany", new_record)

          let data = { tags: this.selected_tags }

          this.$store.dispatch("data/updateRecord", { record_id: this.record.id, data: data }).then((res) => {
            //this.$emit("done")
            this.$store.dispatch("data/emitRecordUpdatedEmitters", res.data)
          })
        }
      }
    }
  }
}
</script>
